import { Button, Container, Text, Link, Message } from "basis";
import { useState } from "react";
import { useMutation } from "react-query";
import { useEventTracking } from "react-event-tracker";
import { isEmail } from "library/validator";
import { useAuthentication } from "library/hooks/auth";
import { Input } from "../../Input/Input";
import { PasswordInput } from "../../PasswordInput/PasswordInput";
import styles from "./login-widget.module.scss";

const loginErrors = {
  UserNotFoundException:
    "Your Latitude ID or password is incorrect. Please check your details and try again.",
  NotAuthorizedException:
    "Your Latitude ID or password is incorrect. Please check your details and try again.",
  InvalidPasswordException:
    "Your Latitude ID or password is incorrect. Please check your details and try again.",
  LimitExceededException:
    "You've tried to log in too many times. Please try again in 5 minutes.",
  InvalidParameterException:
    "Your Latitude ID or password is incorrect. Please check your details and try again.",
};

export function LoginWidget({ className }) {
  const [email, setEmail] = useState({
    value: "",
    success: false,
    fieldError: "",
  });
  const [password, setPassword] = useState({
    value: "",
    success: false,
    fieldError: "",
  });
  const [loginError, setLoginError] = useState(false);
  const { loginUser } = useAuthentication();

  const { trackEvent } = useEventTracking();

  const loginMutation = useMutation(loginUser, {
    onError: (err) => {
      const errorMessage =
        loginErrors[err.code] ?? loginErrors.InvalidParameterException;
      setLoginError(errorMessage);
      setPassword({
        value: "",
        success: false,
        fieldError: "",
      });
    },
  });

  function isValid() {
    return email.success && password.success;
  }

  function triggerFieldErrors() {
    if (!email.success) {
      setEmail({
        fieldError:
          "Please enter your Latitude ID (the email address you used to sign up to the app)",
      });
    }
    if (!password.success) {
      setPassword({ fieldError: "Please enter your password" });
    }
  }

  async function login(e) {
    e.preventDefault();

    if (!isValid()) {
      triggerFieldErrors();
      return false;
    }

    await loginMutation.mutate({
      id: email.value,
      password: password.value,
    });
    return false; // edge fix
  }

  return (
    <form className={`${styles.wrapper} ${className}`} onSubmit={login}>
      <Text as="h2" textStyle="heading4">
        Login
      </Text>
      <Text textStyle="body2" margin="5 0 0 0">
        <strong>
          If you have used the Latitude mobile app before, you can use the same
          Latitude ID to sign in here.
        </strong>
      </Text>
      <Text textStyle="body2" margin="5 0 5 0">
        <strong>First time here? </strong>
        <Link
          href="/register"
          newTab={false}
          onClick={() =>
            trackEvent({
              event: {
                category: "cta",
                action: "registration-link",
                location: "login",
                label: "Register now for a Latitude ID",
              },
            })
          }
        >
          Register now for a Latitude ID
        </Link>
      </Text>
      {loginError && (
        <Message
          severity="blocking"
          bg="secondary.pink.t30"
          testId="error-message"
        >
          {loginError}
        </Message>
      )}
      <Input
        label="Latitude ID"
        className={styles.inputField}
        onChange={setEmail}
        disabled={loginMutation.isLoading}
        error={email.fieldError}
        type="email"
        validator={isEmail}
        placeholder="example@youremail.com"
      />
      <PasswordInput
        error={password.fieldError}
        onChange={setPassword}
        disabled={loginMutation.isLoading}
        className={styles.passwordInput}
      />
      <Text textStyle="body2" margin="1 0 0 0">
        <Link
          href="/forgot-password"
          newTab={false}
          onClick={() =>
            trackEvent({
              event: {
                category: "cta",
                action: "internal-link",
                location: "login",
                label: "Forgot password?",
              },
            })
          }
        >
          Forgot password?
        </Link>
      </Text>
      <Container margin="8 0 0 0">
        <Button width="100%" type="submit" loading={loginMutation.isLoading}>
          Login
        </Button>
      </Container>
    </form>
  );
}
